"use client";

import * as React from "react";
import { Building2, CheckCircle2, Circle, User2 } from "lucide-react";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/shadcnComponents/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcnComponents/ui/table";
import {
  CurrencySelector,
  type Currency,
  currencies,
} from "./currency-selector";
import { useBrand } from "@/Providers/Brand";
import { useCallback, useEffect, useState } from "react";
import { useGeneralSettings } from "@/Providers/General";
import { useNavigate } from "react-router-dom";
import { usePayment } from "@/Providers/Payment";
import { useAuth } from "@/Providers/Auth";
import { cn } from "@/lib/utils";
import { IoMdArrowRoundBack } from "react-icons/io";
import { BrandSignUpHeader } from "../../SignUp/BrandSignUp";
import { useCoupon } from "@/Providers/Coupon";

const billingCycles = ["Monthly", "Quarterly", "Yearly"] as const;
type BillingCycle = (typeof billingCycles)[number];

// Constants
const numberOfDays = { Monthly: 20, Quarterly: 90, Yearly: 365 };
export function PricingContent() {
  const brandState = useBrand();
  const generalState = useGeneralSettings();
  const navigate = useNavigate();
  const paymentState = usePayment();
  const authState = useAuth();
  const couponState = useCoupon();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [Brand, SetBrand] = useState<any>({});
  const [plans, setPlans] = React.useState([]);
  const [addOns, setAddOns] = React.useState([]);
  const [comparisonData, setComparisonData] = React.useState([]);
  const [billingCycle, setBillingCycle] =
    React.useState<BillingCycle>("Monthly");
  const [selectedCurrency, setSelectedCurrency] =
    React.useState<Currency>("USD");
  const [loading, setLoading] = React.useState(false);

  const formatPrice = (price: number) => {
    const convertedPrice =
      price * generalState?.exchangeRates[selectedCurrency];
    const currencySymbol = currencies.find(
      (c) => c.value === selectedCurrency
    )?.symbol;

    if (selectedCurrency === "INR") {
      return `${currencySymbol}${Math.round(convertedPrice).toLocaleString(
        "en-IN"
      )}`;
    }

    return `${currencySymbol}${convertedPrice.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  };

  // Function to get subscription plans
  const getAllSubscriptionPlans = async () => {
    const response = await brandState?.getSubscriptionPlans();
    if (response?.success) {
      setPlans(response.data.plans);
      setAddOns(response.data.addOns);
      setComparisonData(response.data.comparisonData);
    } else {
      console.log("Error fetching subscription plans", response);
    }
  };

  const handleClick = async (psp, planName, planID) => {
    setLoading(true);
    if (planName === "Free Trial" && psp === 0) {
      generalState?.setLoading(true);
      const order = await paymentState?.createBrandEasebuzzOrder(
        "SubscriptionPlanBought",
        psp,
        Brand?.CompanyName,
        authState?.loggedBrand?.email,
        "Purchase Subscription",
        Date.now() + numberOfDays[billingCycle] * 24 * 60 * 60 * 1000,
        Brand?.companyPhoneNumber || "6267941318",
        "",
        planID
      );
      if (order?.success) {
        authState?.setReFetchBrandData((prev) => !prev);
        generalState?.setLoading(false);
        setLoading(false);
        navigate(`/Brand/pricing/checkout/success/?plan=${planName}`);
      } else {
        generalState?.setLoading(false);
        setLoading(false);
        generalState?.showErrorToast(order?.error);
      }
    } else {
      setLoading(false);
      generalState?.setLoading(false);
      navigate(`/Brand/pricing/checkout/?plan=${planName}&planID=${planID}`);
    }
  };

  const createAppsumoOrder = useCallback(async (code) => {
    generalState?.setLoading(true);

    const result = await couponState?.checkAndVerifyCouponCodeForBrands(
      code,
      277 * generalState?.exchangeRates["INR"] * 12,
      "6814732fe4c145ec91ace5a3"
    );
    if (result?.success) {
      const order = await paymentState?.createBrandAppSumOrder(
        "SubscriptionPlanBought",
        result?.netToPay,
        authState?.loggedBrand?.PocName,
        authState?.loggedBrand?.email,
        "Purchase Subscription",
        Date.now() + 365 * 24 * 60 * 60 * 1000,
        authState?.loggedBrand?.PocContact || "6267941318",
        code,
        "6814732fe4c145ec91ace5a3",
        "",
        result?.netToPay * 0.18
      );
      if (order?.success) {
        authState?.setReFetchBrandData((prev) => !prev);
        generalState?.setLoading(false);
        setLoading(false);
        localStorage.removeItem("appsumocode");
        navigate(
          `/Brand/pricing/checkout/success/?plan=${"Appsumo Special Plan"}`
        );
      } else {
        generalState?.setLoading(false);
        setLoading(false);
        generalState?.showErrorToast(order?.error);
      }
    } else {
      generalState?.setLoading(false);
      setLoading(false);
      generalState?.showErrorToast(result?.error);
    }
  }, []);

  useEffect(() => {
    const raw = localStorage.getItem("appsumocode");
    if (raw) {
      const { code, expiry } = JSON.parse(raw);
      const now = Date.now();

      if (now < expiry && authState?.loggedBrand?.firstTime) {
        createAppsumoOrder(code);
      }
    }
  }, [authState?.loggedBrand?.firstTime, createAppsumoOrder]);

  useEffect(() => {
    const initializeData = async () => {
      // Fetch subscription plans
      await getAllSubscriptionPlans();
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function getBrand() {
      const result = await brandState?.getBrandInfo();
      if (result?.success) {
        SetBrand(result?.data1);
      } else {
        generalState?.showErrorToast(result?.error);
      }
    }
    getBrand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {!window.location.pathname
        ?.toLocaleLowerCase()
        ?.includes("dashboard") && <BrandSignUpHeader />}
      <section className="w-full py-6 md:py-12 lg:py-16 bg-gray-50">
        <div className="container px-4 md:px-6">
          {!window.location.pathname?.toLowerCase()?.includes("dashboard") &&
            authState?.loggedBrand.activePlan && (
              <Button
                className="gap-2 text-[#AFAFAF] text-sm not-italic font-medium leading-6"
                onClick={() => {
                  navigate("/Brand/DashBoard");
                }}
                variant="link"
              >
                <IoMdArrowRoundBack />
                Back to Dashboard
              </Button>
            )}
          <div className="flex flex-col items-center justify-center space-y-4 text-center">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl/none">
                Pricing made for everyone
              </h1>
              <p className="mx-auto max-w-[700px] text-gray-500 md:text-xl">
                Whether you're a startup, growing business, or enterprise
                company, we have a plan that's right for you.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-6 md:py-12 lg:py-16">
        <div className="container px-4 md:px-6">
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
            <div className="flex gap-2">
              {billingCycles?.map((cycle) => (
                <Button
                  key={cycle}
                  variant={billingCycle === cycle ? "default" : "outline"}
                  onClick={() => setBillingCycle(cycle)}
                >
                  {cycle}
                </Button>
              ))}
            </div>
            <CurrencySelector onCurrencyChange={setSelectedCurrency} />
          </div>

          <div
            className={cn(
              "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4",
              `${
                authState?.loggedBrand?.activePlan
                  ? "lg:grid-cols-3"
                  : "lg:grid-cols-4"
              }`
            )}
          >
            {plans
              ?.filter((plan) =>
                authState?.loggedBrand?.activePlan
                  ? plan?.name !== "Free Trial"
                  : true
              )
              .map((plan) => (
                <Card
                  key={plan?.name}
                  className={`flex flex-col ${
                    plan?.popular ? "border-primary shadow-lg" : ""
                  }`}
                >
                  <CardHeader>
                    {plan?.name === "Free Trial" ? (
                      <User2 className="h-10 w-10" />
                    ) : (
                      <Building2 className="h-10 w-10" />
                    )}
                    {plan?.popular && (
                      <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-primary text-primary-foreground hover:bg-primary/80">
                        Most Popular
                      </div>
                    )}
                    <CardTitle>{plan?.name}</CardTitle>
                    <CardDescription>{plan?.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <div className="mt-4 flex items-baseline text-gray-900">
                      <span className="text-3xl font-bold tracking-tight">
                        {plan?.name !== "Free Trial" && (
                          <span className="text-xl text-gray-500 line-through mr-2">
                            {formatPrice(plan?.slashedPrice?.[billingCycle])}
                          </span>
                        )}
                        {formatPrice(plan?.price?.[billingCycle])}
                      </span>
                      <span className="ml-1 text-xl font-semibold">
                        /{"Monthly".toLowerCase()}
                      </span>
                    </div>
                    <ul className="mt-6 space-y-4">
                      {plan?.features?.map((feature) => (
                        <li key={feature} className="flex text-sm">
                          <CheckCircle2 className="mr-2 h-5 w-5 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button
                      disabled={
                        loading || authState?.loggedBrand?.isSampleBrand
                      }
                      onClick={() =>
                        handleClick(
                          plan?.price?.[billingCycle],
                          plan?.name,
                          plan?.name === "Free Trial"
                            ? plan?.id["Monthly"]
                            : plan?.id[billingCycle]
                        )
                      }
                      className="w-full"
                      variant={plan?.popular ? "default" : "outline"}
                    >
                      {"Continue"}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
          </div>
        </div>
      </section>

      <section className="w-full py-6 md:py-12 lg:py-16 bg-gray-50">
        <div className="container px-4 md:px-6">
          <h2 className="text-2xl font-bold tracking-tight text-center mb-8">
            Compare Plans
          </h2>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[300px]">Feature</TableHead>
                  {Object.keys(plans).map((_, index) => (
                    <TableHead key={plans[index].name} className="text-center">
                      {plans[index].name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="text-left space-y-1">
                        <div className="font-semibold text-base">
                          {row?.feature}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {row?.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {typeof row["Free Trial"] === "boolean" ? (
                        row["Free Trial"] ? (
                          <CheckCircle2 className="mx-auto h-5 w-5 text-green-500" />
                        ) : (
                          <Circle className="mx-auto h-5 w-5 text-gray-300" />
                        )
                      ) : (
                        row["Free Trial"]
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {typeof row?.Basic === "boolean" ? (
                        row?.Basic ? (
                          <CheckCircle2 className="mx-auto h-5 w-5 text-green-500" />
                        ) : (
                          <Circle className="mx-auto h-5 w-5 text-gray-300" />
                        )
                      ) : (
                        row?.Basic
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {typeof row?.Pro === "boolean" ? (
                        row?.Pro ? (
                          <CheckCircle2 className="mx-auto h-5 w-5 text-green-500" />
                        ) : (
                          <Circle className="mx-auto h-5 w-5 text-gray-300" />
                        )
                      ) : (
                        row?.Pro
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {typeof row?.Premium === "boolean" ? (
                        row?.Premium ? (
                          <CheckCircle2 className="mx-auto h-5 w-5 text-green-500" />
                        ) : (
                          <Circle className="mx-auto h-5 w-5 text-gray-300" />
                        )
                      ) : (
                        row?.Premium
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={6} className="py-4">
                    <h3 className="text-lg font-semibold mb-2">Add-Ons</h3>
                    <div className="space-y-2">
                      {addOns.map((addon, index) => (
                        <div
                          key={index}
                          className="flex items-start justify-between"
                        >
                          <div className="text-left">
                            <p className="font-medium">{addon?.feature}</p>
                            <p className="text-xs text-muted-foreground">
                              {addon?.description}
                            </p>
                          </div>
                          <span className="font-medium">
                            {formatPrice(addon?.price)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      * Includes individual reports, engagement analysis, and
                      integration into the overall campaign insights
                    </p>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </section>
    </div>
  );
}
